import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "sonner";
import MaintenanceGate from "./components/app/MaintenanceGate";

import Landing from "./pages/Landing";
import Browse from "./pages/Browse";
import CampsiteDetail from "./pages/CampsiteDetail";
import Compare from "./pages/Compare";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Favorites from "./pages/Favorites";
import Profile from "./pages/Profile";
import AuthCallback from "./pages/AuthCallback";
import Community from "./pages/Community";
import CommunityThread from "./pages/CommunityThread";
import Wall from "./pages/Wall";
import Messages from "./pages/Messages";
import ChatRoom from "./pages/ChatRoom";
import Beta from "./pages/Beta";
import Admin from "./pages/Admin";
import CompareShare from "./pages/CompareShare";
import Pro from "./pages/Pro";
import BillingSuccess from "./pages/BillingSuccess";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import Guidelines from "./pages/Guidelines";
import Accessibility from "./pages/Accessibility";
import Poster from "./pages/Poster";
import Leaflet from "./pages/Leaflet";
import BusinessCards from "./pages/BusinessCards";
import LaunchPack from "./pages/LaunchPack";
import ReelCard from "./pages/ReelCard";
import ReelsKit from "./pages/ReelsKit";
import ReelsVariety from "./pages/ReelsVariety";
import LaunchPush from "./pages/LaunchPush";
import FinalCallReel from "./pages/FinalCallReel";
import LaunchDayReel from "./pages/LaunchDayReel";
import FreeVsProReel from "./pages/FreeVsProReel";
import PartnerShopsReel from "./pages/PartnerShopsReel";
import PollVoteReel from "./pages/PollVoteReel";
import ShareTripPhotoReel from "./pages/ShareTripPhotoReel";
import HowItWorksReel from "./pages/HowItWorksReel";
import TryProFreeReel from "./pages/TryProFreeReel";
import JoinTheChatReel from "./pages/JoinTheChatReel";
import MountainRescueReel from "./pages/MountainRescueReel";
import CampsitePromoReel from "./pages/CampsitePromoReel";
import PollVotePost from "./pages/PollVotePost";
import FishingCampingReel from "./pages/FishingCampingReel";
import FrowenFieldsReel from "./pages/FrowenFieldsReel";
import ShareYourTripReel from "./pages/ShareYourTripReel";
import SimplifiedSearchReel from "./pages/SimplifiedSearchReel";
import TagYourMates from "./pages/TagYourMates";
import PinterestPins from "./pages/PinterestPins";
import InstagramReelStudio from "./pages/InstagramReelStudio";
import Fisheries from "./pages/Fisheries";
import Charities from "./pages/Charities";
import AutumnCampingReel from "./pages/AutumnCampingReel";
import AugustWarmWeatherReel from "./pages/AugustWarmWeatherReel";
import Tips from "./pages/Tips";
import ShareYourTrip from "./pages/ShareYourTrip";
import SeasonalGuide from "./pages/SpringGuide"; // iter211 — file kept for git history; renders current-season guide
import SecretPitchMap from "./pages/SecretPitchMap";

import Festivals from "./pages/Festivals";
import FestivalDetail from "./pages/FestivalDetail";
import FestivalReliefReel from "./pages/FestivalReliefReel";
import Vote from "./pages/Vote";
import VoteOne from "./pages/VoteOne";
import Checklist from "./pages/Checklist";
import Magic from "./pages/Magic";
import ChatWidget from "./components/app/ChatWidget";
import CookieBanner from "./components/app/CookieBanner";
import BetaGate from "./components/app/BetaGate";
import PwaInstallToast from "./components/app/PwaInstallToast";
import BirthdayLoginToast from "./components/app/BirthdayLoginToast";
import CapacitorBoot from "./components/app/CapacitorBoot";
import TrialActivationNudge from "./components/app/TrialActivationNudge";
import WallLoginPrompt from "./components/app/WallLoginPrompt";
import BookingCheckPrompt from "./components/app/BookingCheckPrompt";
import FreshContentChip from "./components/app/FreshContentChip";
import AnonymousGate from "./components/app/AnonymousGate";
import GlobalSignupPromptModal from "./components/app/GlobalSignupPromptModal";
import PresenceTracker from "./components/app/PresenceTracker";
import AffiliatePromoBanner from "./components/app/AffiliatePromoBanner";
import GuestBanner from "./components/app/GuestBanner";
import WelcomeUnlockedModal from "./components/app/WelcomeUnlockedModal";
import UnlockEverythingReel from "./pages/UnlockEverythingReel";
import WhyJoinNowReel from "./pages/WhyJoinNowReel";
import TevaOutletReel from "./pages/TevaOutletReel";
import LockedFeaturesReel from "./pages/LockedFeaturesReel";

function InnerRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <BirthdayLoginToast />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/beta" element={<Beta />} />
      {/* iter189 — Gated routes: full app access requires a (free) account.
          Any existing member (including "free for life" grandfathered
          accounts) bypasses AnonymousGate the moment AuthContext resolves.
          iter219 — /browse is UN-gated. It's the primary discovery
          surface — locking it forced anonymous visitors to see only the
          homepage top-5 and pushed them off the site. Anons can now
          browse the whole 22,000-site catalogue; interactive actions
          (favourite / message / trip dates) still show the sign-in
          prompt at the point of use. Same reasoning for /fisheries. */}
      <Route path="/browse" element={<Browse />} />
      <Route path="/campsites/:id" element={<CampsiteDetail />} />
      <Route path="/compare" element={<AnonymousGate><Compare /></AnonymousGate>} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/favorites" element={<AnonymousGate><Favorites /></AnonymousGate>} />
      <Route path="/profile" element={<AnonymousGate><Profile /></AnonymousGate>} />
      <Route path="/community" element={<Community />} />
      <Route path="/charities" element={<Charities />} />
      <Route path="/share/mountain-rescue" element={<MountainRescueReel />} />
      <Route path="/share/autumn-camping" element={<AutumnCampingReel />} />
      <Route path="/share/august-warm-weather" element={<AugustWarmWeatherReel />} />
      <Route path="/tips" element={<Tips />} />
      <Route path="/community/:id" element={<CommunityThread />} />
      <Route path="/wall" element={<Wall />} />
      {/* iter210 — Public SEO deep-link landing. External posts / QR codes /
          reels can point here to convert visitors into signups in one flow. */}
      <Route path="/share-your-trip" element={<ShareYourTrip />} />
      <Route path="/share/trip" element={<ShareYourTrip />} />
      {/* iter211 — Seasonal PDF lead-magnet landing (email capture in exchange for a shareable guide) */}
      <Route path="/autumn-guide" element={<SeasonalGuide />} />
      <Route path="/winter-guide" element={<SeasonalGuide />} />
      <Route path="/seasonal-guide" element={<SeasonalGuide />} />
      {/* iter212 — Secret Pitch Map lead magnet (email → PDF + drip funnel) */}
      <Route path="/secret-pitch-map" element={<SecretPitchMap />} />
      <Route path="/pitch-map" element={<SecretPitchMap />} />
      <Route path="/wild-camping-map" element={<SecretPitchMap />} />
      <Route path="/messages" element={<AnonymousGate><Messages /></AnonymousGate>} />
      <Route path="/chatroom" element={<AnonymousGate><ChatRoom /></AnonymousGate>} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/share/:slug" element={<CompareShare />} />
      <Route path="/pro" element={<Pro />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/guidelines" element={<Guidelines />} />
      <Route path="/accessibility" element={<Accessibility />} />
      <Route path="/print/poster" element={<Poster />} />
      <Route path="/print/leaflet" element={<Leaflet />} />
      <Route path="/print/cards" element={<BusinessCards />} />
      <Route path="/share/reel-card" element={<ReelCard />} />
      <Route path="/share/reels-kit" element={<ReelsKit />} />
      <Route path="/share/reels-variety" element={<ReelsVariety />} />
      <Route path="/share/launch-push" element={<LaunchPush />} />
      <Route path="/share/final-call" element={<FinalCallReel />} />
      <Route path="/share/launch-day" element={<LaunchDayReel />} />
      <Route path="/share/free-vs-pro" element={<FreeVsProReel />} />
      <Route path="/share/partner-shops" element={<PartnerShopsReel />} />
      <Route path="/share/vote-on-poll" element={<PollVoteReel />} />
      <Route path="/share/share-a-trip" element={<ShareTripPhotoReel />} />
      <Route path="/share/how-it-works" element={<HowItWorksReel />} />
      <Route path="/share/try-pro-free" element={<TryProFreeReel />} />
      <Route path="/share/join-the-chat" element={<JoinTheChatReel />} />
      <Route path="/share/dulas-glamping" element={<Landing />} />
      <Route path="/share/promo/:campsiteId" element={<CampsitePromoReel />} />
      <Route path="/share/poll-vote" element={<PollVotePost />} />
      <Route path="/share/fishing-camping" element={<FishingCampingReel />} />
      <Route path="/share/frowen-fields" element={<FrowenFieldsReel />} />
      <Route path="/share/frowen-fields-reel" element={<FrowenFieldsReel />} />
      <Route path="/share/share-your-trip" element={<ShareYourTripReel />} />
      <Route path="/share/simplified-search" element={<SimplifiedSearchReel />} />
      {/* iter189 — signup-conversion + Teva Outlet reels */}
      <Route path="/share/unlock-everything" element={<UnlockEverythingReel />} />
      <Route path="/share/why-join-now" element={<WhyJoinNowReel />} />
      <Route path="/share/teva-outlet" element={<TevaOutletReel />} />
      <Route path="/share/locked-features" element={<LockedFeaturesReel />} />
      <Route path="/tag" element={<TagYourMates />} />
      <Route path="/tag-your-mates" element={<TagYourMates />} />
      <Route path="/admin/pinterest-pins" element={<PinterestPins />} />
      <Route path="/admin/instagram-reels/:slug" element={<InstagramReelStudio />} />
      <Route path="/fisheries" element={<Fisheries />} />
      {/* iter122 — Festival hub */}
      <Route path="/festivals" element={<Festivals />} />
      <Route path="/festivals/:slug" element={<FestivalDetail />} />
      <Route path="/share/festival-relief" element={<FestivalReliefReel />} />
      <Route path="/vote" element={<Vote />} />
      <Route path="/share/vote" element={<Vote />} />
      <Route path="/v/:option" element={<VoteOne />} />
      <Route path="/checklist" element={<AnonymousGate><Checklist /></AnonymousGate>} />
      <Route path="/magic" element={<Magic />} />
      <Route path="/launch-pack" element={<LaunchPack />} />
    </Routes>
    </>
  );
}

// Keeps pop-ups and banners off the reel studio so screen recordings stay clean.
function StudioHidden({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith("/admin/instagram-reels/") ? null : children;
}

export default function App() {
  // iter53 — clear the blank-page-recovery flag in /public/index.html as
  // soon as React mounts. If this never runs (e.g. cached HTML pointing
  // to a missing JS bundle), the 6-second timer fires, purges all SWs +
  // caches, and reloads. Guarantees no user is ever stuck on a blank
  // screen after a deploy.
  if (typeof window !== "undefined") window.__ccMounted = true;
  return (
    <BrowserRouter>
      <MaintenanceGate>
        <AuthProvider>
          <BetaGate>
            <StudioHidden><AffiliatePromoBanner /><GuestBanner /></StudioHidden>
            <InnerRouter />
            <StudioHidden>
              <ChatWidget />
              <CookieBanner />
              <PwaInstallToast />
            </StudioHidden>
            <CapacitorBoot />
            <StudioHidden>
              <TrialActivationNudge />
              <GlobalSignupPromptModal />
              <WallLoginPrompt />
              <BookingCheckPrompt />
              <FreshContentChip />
              <WelcomeUnlockedModal />
            </StudioHidden>
            <PresenceTracker />
          </BetaGate>
          <Toaster
            position="top-center"
            richColors
            closeButton
            offset={16}
            toastOptions={{
              style: { maxWidth: "calc(100vw - 32px)" },
            }}
          />
        </AuthProvider>
      </MaintenanceGate>
    </BrowserRouter>
  );
}
